import {
  NotificationDisplayServiceV1,
  NotificationTypeV1,
} from '@oneaudi/audi-notification-display-service';
import { useI18n } from '@oneaudi/i18n-context';
import {
  SYS_COLOR_TEXT_HEADLINE_DEFAULT_DARK,
  SYS_SPACE_FIXED_300,
  SYS_SPACE_RELATIVE_LG,
  SYS_SPACE_RELATIVE_XL,
} from '@oneaudi/unified-web-alpha-common';
import { Card, Text, TextButton, TextLink } from '@oneaudi/unified-web-alpha-components';
import { dynamicStylingHelper, StyledHeadline, useServicesContext } from '@oneaudi/vtp-shared';
import React, { useEffect } from 'react';
import styled from 'styled-components';

export const LocationRequired = ({
  onAction,
  showNotification = true,
}: {
  onAction: () => void;
  showNotification?: boolean;
}) => {
  const addLocationLabel = useI18n({
    id: `nemo.ui.sc.mas.toolbar.addLocation`,
    defaultMessage: 'Add location',
  });
  const locationRequiredLabel = useI18n({
    id: 'nemo.ui.sc.mas.toolbar.locationRequired',
    defaultMessage: 'Location required',
  });

  const removedLocationLabel = useI18n({
    id: 'nemo.ui.sc.mas.toolbar.removedLocation',
    defaultMessage:
      "You've removed the city or ZIP code. Please add a location to continue, either a city or ZIP code.",
  });

  const HeadlineWrapper = styled.div`
    width: 100%;
    padding: ${SYS_SPACE_RELATIVE_XL} ${SYS_SPACE_RELATIVE_LG};
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: ${SYS_SPACE_FIXED_300};
  `;

  const StyledText = styled(Text)`
    ${dynamicStylingHelper('', `color: ${SYS_COLOR_TEXT_HEADLINE_DEFAULT_DARK};`)};
  `;

  const StyledTextButton = styled.div`
    display: flex;
    flex-direction: row;
  `;

  const notificationDisplayService = useServicesContext().getAdditionalService(
    'notification-display-service'
  ) as NotificationDisplayServiceV1;

  const NOTIFICATION_ID = 'vtp-myaudi-location-required';

  useEffect(() => {
    if (!showNotification) {
      notificationDisplayService.closeNotification(NOTIFICATION_ID);
      return () => notificationDisplayService.closeNotification(NOTIFICATION_ID);
    }

    notificationDisplayService.closeNotification(NOTIFICATION_ID);
    notificationDisplayService.createNotification(
      NotificationTypeV1.POPUP_ERROR,
      NOTIFICATION_ID,
      removedLocationLabel,
      {
        title: locationRequiredLabel,
        textButton: (
          <TextLink size="sm" aria-label={addLocationLabel} variant="standalone" onClick={onAction}>
            {addLocationLabel}
          </TextLink>
        ),
      }
    );
    return () => notificationDisplayService.closeNotification(NOTIFICATION_ID);
  }, [
    addLocationLabel,
    locationRequiredLabel,
    notificationDisplayService,
    onAction,
    removedLocationLabel,
    showNotification,
  ]);

  return (
    <Card>
      <HeadlineWrapper>
        <StyledHeadline
          tag="h2"
          aria-label={locationRequiredLabel}
          variant={{
            order: '2',
            type: 'headline',
            weight: 'normal',
          }}
        >
          {locationRequiredLabel}
        </StyledHeadline>
        <StyledText
          tag="span"
          aria-label={removedLocationLabel}
          variant={{
            order: '1',
            style: 'normal',
          }}
        >
          {removedLocationLabel}
        </StyledText>
        <StyledTextButton>
          <TextButton
            aria-label={addLocationLabel}
            variant="primary"
            size="md"
            onClick={onAction}
            data-testid="reset"
            isFullWidth={false}
          >
            {addLocationLabel}
          </TextButton>
        </StyledTextButton>
      </HeadlineWrapper>
    </Card>
  );
};
